import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { Download, Pencil } from "lucide-react";
import {
  Badge,
  Button,
  ChartTooltipContent,
  GappedChart,
} from "web-design-system";
import { usePortfolioReviews } from "creso-core/hooks";
import { PortfolioReviewListItem } from "creso-core/types";
import { formatAsPercentage, formatShort } from "creso-core/utils";
import { ALLOCATION_COLORS, REVIEW_SOURCE_LABEL } from "../constants";

export function PastReviews({ clientId }: { clientId: number }) {
  const { data } = usePortfolioReviews({ client_id: clientId });

  if (!data?.results.length) return null;

  return (
    <section className="flex flex-col gap-4">
      <h5>Past reviews</h5>
      <div className="flex flex-col divide-y divide-border">
        {data.results.map((review) => (
          <PastReviewRow key={review.id} review={review} />
        ))}
      </div>
    </section>
  );
}

function PastReviewRow({ review }: { review: PortfolioReviewListItem }) {
  const summary = "equity_pct" in review.summary ? review.summary : null;
  const date = review.as_of_date ?? review.created_at;

  return (
    <div className="grid grid-cols-1 items-center gap-6 px-4 py-6 md:grid-cols-[14rem_1fr_14rem_auto]">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
            {date ? format(new Date(date), "d MMM, yyyy") : "-"}
          </span>
          {review.status === "DRAFT" ? (
            <Badge
              variant="custom"
              className="bg-tag-yellow text-tag-yellow-foreground"
            >
              Draft
            </Badge>
          ) : null}
        </div>
        <span className="text-xs text-muted-foreground">
          {REVIEW_SOURCE_LABEL[review.source]}
        </span>
      </div>

      <div className="flex max-w-90 flex-col gap-2">
        {summary ? (
          <>
            <span className="text-xs">
              After review: {formatAsPercentage(summary.equity_pct, "-", 0)}{" "}
              Equity
            </span>
            <GappedChart
              className="relative h-1.5 w-full"
              segments={summary.allocation_after.map((bucket, index) => ({
                key: bucket.key,
                label: bucket.label,
                value: bucket.pct,
                color: ALLOCATION_COLORS[index % ALLOCATION_COLORS.length],
              }))}
              tooltipContent={
                <ChartTooltipContent
                  hideLabel
                  valueFormatter={(v) => formatAsPercentage(Number(v), "-", 0)}
                />
              }
            />
          </>
        ) : null}
      </div>

      <div className="flex flex-col gap-1 md:items-end">
        {summary ? (
          <>
            <span className="text-sm font-semibold">
              {formatShort(summary.amount_moved)} moved
            </span>
            <span className="text-xs text-muted-foreground">
              {summary.switch_or_exit_count} funds to switch or exit
            </span>
          </>
        ) : null}
      </div>

      <div className="flex gap-4">
        <Button variant="outline" size="icon" aria-label="Edit review" asChild>
          <Link
            to="/clients/$id/review/$reviewId"
            params={{
              id: String(review.client_id),
              reviewId: String(review.id),
            }}
          >
            <Pencil />
          </Link>
        </Button>
        <Button variant="outline" size="icon" aria-label="Download report">
          <Download />
        </Button>
      </div>
    </div>
  );
}
